import React, { useMemo, useState } from 'react';
import { Flex, Divider, useMediaQuery, Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import BECarousel from '@molecules/BECarousel';
import BEImage from '@molecules/BEImage';
import BEText from '@molecules/Text';
import BEStepper from '@molecules/Stepper';
import BEButton from '@molecules/Button/Button';
import BEWeekdays from '@molecules/BEWeekdays';
import ModalComponent, { ModalProps } from '@molecules/BEOffScreen/Modal';
import ScreenSize from '../../../constants/screen-sizes';
import Addon from '@data/models/Addon';
import { useTranslation } from 'react-i18next';
import Text from '@molecules/Text';
import { useStore } from '@data/other/withStore';
import { observer } from 'mobx-react';
import { useViewportSize } from '@view/hooks/useViewportSize';

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: 24px;
`;

const StyledPriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding-top: 4px;
  padding-bottom: 12px;
`;

const ModalAddonDetailed = (props: ModalProps & Addon) => {
  const {
    name,
    rate,
    chargeType,
    weekAvailability = [],
    description,
    images,
    onCancel,
  } = props;

  const { property } = useStore();
  const { cart } = property;

  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);
  const { width } = useViewportSize();
  const modalWidth = isMobile ? width : 560;
  const { t } = useTranslation('common');

  const [amount, setAmount] = useState<number>(1);

  const onCart = useMemo(() => {
    return cart.items.findIndex((x) => x.getItemId() === props.id) >= 0;
  }, [cart.items]);

  return (
    <ModalComponent
      {...props}
      hideHeader={true}
      hideFooter={true}
      width={modalWidth}
      bodyPadding={false}
    >
      {images.length === 0 && <BEImage width={modalWidth} height={300} />}
      {images.length > 0 && (
        <BECarousel
          gap={0}
          itemWidth={modalWidth}
          itemHeight={300}
          multiplier={1}
          constraint={1}
        >
          {images.map((image, index) => (
            <Flex
              key={index}
              shadow="md"
              w="100%"
              h="100%"
              borderTopRightRadius="8px"
              borderTopLeftRadius="8px"
              overflow="hidden"
            >
              <BEImage
                src={image.src}
                alt={image.alt}
                fit="cover"
                width={modalWidth}
                height={300}
              />
            </Flex>
          ))}
        </BECarousel>
      )}
      <StyledContent>
        <BEText type={isMobile ? 'h3' : 'h1'}>{name.getText()}</BEText>
        <StyledPriceWrapper>
          <BEText type="h3">${rate}</BEText>
          <BEText type="card-body" color="neutral" darkness="700">
            {t(`addon-${chargeType}`)}
          </BEText>
        </StyledPriceWrapper>
        {[
          'per_night',
          'per_accommodation_per_night',
          'per_guest_per_night',
        ].includes(chargeType) && (
          <Box mb="8px">
            <BEWeekdays weekdays={weekAvailability} />
          </Box>
        )}
        <Text html={description.getText()} />
      </StyledContent>
      <Divider />
      <Flex
        paddingX={6}
        paddingY={18}
        justifyContent={isMobile ? 'flex-end' : 'space-between'}
      >
        {!isMobile && (
          <BEButton color="neutral" variant="outline" onClick={onCancel}>
            Cancel
          </BEButton>
        )}
        <Flex gap={4}>
          {chargeType === 'quantity' && (
            <BEStepper
              size="md"
              value={amount}
              onChange={setAmount}
              minValue={1}
              maxValue={
                props.maxQtyPerReservation === 0
                  ? undefined
                  : props.maxQtyPerReservation
              }
            />
          )}

          {!onCart ? (
            <BEButton
              color="primary"
              variant="outline"
              onClick={() => cart.addAddon(props, amount)}
            >
              {t('select')}
            </BEButton>
          ) : (
            <BEButton
              color="primary"
              variant="outline"
              onClick={() => cart.removeAddon(props)}
              icon="trash-can"
            >
              {t('remove')}
            </BEButton>
          )}
        </Flex>
      </Flex>
    </ModalComponent>
  );
};

export default observer(ModalAddonDetailed);
